import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, resolveTenantSlug, tokens, apiUrl, asList } from '../lib/api';
import type {
  CampusView, InstitutionView, AcademicClassView, EligibilityReport, StudentEligibilityView,
} from '../lib/types';

const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

const BAND_STYLE: Record<string, { label: string; cls: string }> = {
  Eligible: { label: 'Eligible', cls: 'pill-ok' },
  Condonation: { label: 'Condonation', cls: 'pill-warn' },
  Detained: { label: 'Detained', cls: 'pill-danger' },
  NotApplicable: { label: 'No data', cls: 'pill-muted' },
};

export function EligibilityPage() {
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [campuses, setCampuses] = useState<CampusView[]>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [classId, setClassId] = useState('');
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [report, setReport] = useState<EligibilityReport | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get<InstitutionView[]>('/api/institutions'),
      api.get<CampusView[]>('/api/campuses'),
    ]).then(([i, c]) => { setInstitutions(i); setCampuses(c); if (i.length === 1) setInstitutionId(i[0].id); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!institutionId) { setClasses([]); return; }
    api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`).then((r) => setClasses(asList(r))).catch(() => {});
  }, [institutionId]);

  const run = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ academicClassId: classId, from, to });
      setReport(await api.get<EligibilityReport>(`/api/period-attendance/eligibility?${params}`));
    } finally { setLoading(false); }
  }, [classId, from, to]);

  const exportCsv = () => {
    if (!classId) return;
    // hit the CSV endpoint directly with tenant + auth headers via a fetch + blob download
    const params = new URLSearchParams({ academicClassId: classId, from, to });
    const slug = resolveTenantSlug();
    fetch(apiUrl(`/api/period-attendance/eligibility.csv?${params}`), {
      headers: {
        ...(slug ? { 'X-Tenant': slug } : {}),
        ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
      },
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `eligibility_${from}_${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const campusName = (id: string) => campuses.find((c) => c.id === id)?.name ?? '';
  const detainedCount = report?.students.filter((s) => s.worstBand === 'Detained').length ?? 0;
  const condonationCount = report?.students.filter((s) => s.worstBand === 'Condonation').length ?? 0;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Engineering</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Exam eligibility</h1>
        <p style={{ color: 'var(--ink-2)', marginTop: 4, maxWidth: 640 }}>
          Per-subject attendance against the institution’s thresholds. Only <strong>held</strong> classes
          count; excused sessions are excluded. A student short in any one subject is flagged for that subject.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="label">Institution</label>
          <select className="input" value={institutionId} onChange={(e) => { setInstitutionId(e.target.value); setClassId(''); setReport(null); }} style={{ minWidth: 220 }}>
            <option value="">Select</option>
            {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}{campusName(i.campusId) ? ` · ${campusName(i.campusId)}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Class</label>
          <select className="input" value={classId} onChange={(e) => { setClassId(e.target.value); setReport(null); }} disabled={!institutionId} style={{ minWidth: 200 }}>
            <option value="">Select</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div><label className="label">From</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 160 }} /></div>
        <div><label className="label">To</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 160 }} /></div>
        <button className="btn btn-primary" onClick={run} disabled={!classId || loading}>{loading ? 'Running…' : 'Run report'}</button>
        {report && report.students.length > 0 && <button className="btn" onClick={exportCsv}>Export CSV</button>}
      </div>

      {report && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Summary label="Detained" value={detainedCount} tone="danger" />
            <Summary label="Condonation" value={condonationCount} tone="warn" />
            <Summary label="Threshold" value={`${report.eligibleThreshold}% / ${report.condonationFloor}%`} />
          </div>

          {report.students.length === 0 ? (
            <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>
              No students linked to this class yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {report.students.map((s) => <StudentCard key={s.studentId} s={s} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Summary({ label, value, tone }: { label: string; value: ReactNode; tone?: 'danger' | 'warn' }) {
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn)' : 'var(--ink)';
  return (
    <div className="card" style={{ padding: '12px 18px', minWidth: 130 }}>
      <div className="label" style={{ marginBottom: 6 }}>{label}</div>
      <div className="mono" style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
    </div>
  );
}

function StudentCard({ s }: { s: StudentEligibilityView }) {
  const [open, setOpen] = useState(s.worstBand === 'Detained');
  const band = BAND_STYLE[s.worstBand] ?? BAND_STYLE.NotApplicable;
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        border: 'none', background: 'transparent', padding: '14px 18px', cursor: 'pointer',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{s.fullName}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
        </div>
        <span className={`pill ${band.cls}`}>{band.label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--line)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <Th>Subject</Th><Th align="right">Held</Th><Th align="right">Attended</Th><Th align="right">%</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {s.subjects.map((sub) => {
                const b = BAND_STYLE[sub.band] ?? BAND_STYLE.NotApplicable;
                return (
                  <tr key={sub.subjectId} style={{ borderTop: '1px solid var(--line-2)' }}>
                    <Td>
                      <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{sub.subjectCode}</span>{' '}
                      {sub.subjectName}
                    </Td>
                    <Td align="right"><span className="mono">{sub.held}</span></Td>
                    <Td align="right"><span className="mono">{sub.attended}</span></Td>
                    <Td align="right"><span className="mono" style={{ fontWeight: 700, color: b.cls === 'pill-danger' ? 'var(--danger)' : b.cls === 'pill-warn' ? 'var(--warn)' : 'var(--ink)' }}>{sub.band === 'NotApplicable' ? '—' : `${sub.percent}%`}</span></Td>
                    <Td><span className={`pill ${b.cls}`} style={{ fontSize: 11 }}>{b.label}</span></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '9px 16px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, align }: { children: ReactNode; align?: 'right' }) {
  return <td style={{ padding: '9px 16px', textAlign: align ?? 'left', fontSize: 13 }}>{children}</td>;
}
